import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalActiva, setSucursalActiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [modoEspectador, setModoEspectador] = useState(false);

  // Verificar token al cargar
  useEffect(() => {
    const verificarAuth = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Configurar token en axios
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Obtener datos del usuario
        const response = await api.get('/auth/me');

        if (response.data.success) {
          setUsuario(response.data.data.usuario);
          setSucursales(response.data.data.sucursales || []);

          // FIX: Ensure sucursalActiva is stored and used as proper types
          const sucGuardada = localStorage.getItem('sucursalActiva');
          const sucId = sucGuardada ? parseInt(sucGuardada, 10) : response.data.data.usuario.sucursalId;
          setSucursalActiva(sucId);

          // FIX: Ensure X-Sucursal-Id header is always a string
          api.defaults.headers.common['X-Sucursal-Id'] = String(sucId);

          // Determinar si estamos en modo espectador
          // FIX: super_admin NUNCA está en modo espectador — puede actuar en cualquier sucursal
          const userSucId = response.data.data.usuario.sucursalId;
          const esSuperAdmin = response.data.data.usuario.rol === 'super_admin';
          const puedeVerOtras = esSuperAdmin || response.data.data.usuario.rol === 'admin' || (response.data.data.usuario.permisos || []).includes('*');
          setModoEspectador(puedeVerOtras && !esSuperAdmin && sucId !== userSucId);

          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error verificando auth:', error);

        // FIX: Handle network errors gracefully (no response = network error, CORS, timeout)
        // Only attempt refresh for 401 errors with a valid refreshToken.
        // Network errors (error.response is undefined) should clean up auth
        // to avoid stuck loading states.
        const is401 = error.response?.status === 401;

        if (refreshToken && is401) {
          try {
            const refreshResponse = await api.post('/auth/refresh', { refreshToken });
            if (refreshResponse.data.success) {
              const { token: newToken, refreshToken: newRefresh } = refreshResponse.data.data;
              localStorage.setItem('token', newToken);
              localStorage.setItem('refreshToken', newRefresh);
              api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

              // Reintentar /me
              const meResponse = await api.get('/auth/me');
              if (meResponse.data.success) {
                const usr = meResponse.data.data.usuario;
                setUsuario(usr);
                setSucursales(meResponse.data.data.sucursales || []);
                // FIX: Restore sucursalActiva from localStorage on refresh path too
                const sucGuardada = localStorage.getItem('sucursalActiva');
                const sucId = sucGuardada ? parseInt(sucGuardada, 10) : usr.sucursalId;
                setSucursalActiva(sucId);
                api.defaults.headers.common['X-Sucursal-Id'] = String(sucId);
                setIsAuthenticated(true);
              }
            }
          } catch (refreshError) {
            // Refresh falló, limpiar todo
            limpiarAuth();
          }
        } else {
          // FIX: For network errors (no response), log a warning and clean auth.
          // This prevents the app from being stuck in a loading state forever.
          if (!error.response) {
            console.warn('Network error during auth verification - cleaning up auth state');
          }
          limpiarAuth();
        }
      }

      setLoading(false);
    };

    verificarAuth();
  }, []);

  // FIX: limpiarAuth now clears ALL state including modoEspectador
  const limpiarAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sucursalActiva');
    delete api.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['X-Sucursal-Id'];
    setUsuario(null);
    setSucursales([]);
    setSucursalActiva(null);
    setIsAuthenticated(false);
    setModoEspectador(false);
  };

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });

    if (response.data.success) {
      const { token, refreshToken, usuario: usr, sucursales: sucs } = response.data.data;

      // Guardar tokens
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      // Configurar axios
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // FIX: Ensure sucursalActiva is stored as string in localStorage
      const sucId = usr.sucursalId;
      localStorage.setItem('sucursalActiva', String(sucId));
      api.defaults.headers.common['X-Sucursal-Id'] = String(sucId);

      setUsuario(usr);
      setSucursales(sucs || []);
      setSucursalActiva(sucId);
      setIsAuthenticated(true);

      return { success: true };
    }

    return { success: false, error: response.data.error };
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      // Ignorar errores de logout
    }
    limpiarAuth();
  };

  // Cerrar TODAS las sesiones de este usuario (expulsa a todos los que usan esta cuenta)
  const cerrarTodasSesiones = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await api.post('/auth/logout', { refreshToken, cerrarTodas: true });
    } catch (error) {
      console.error('Error cerrando sesiones:', error);
    }
    limpiarAuth();
  };

  // FIX: Ensure all localStorage values are stored as strings.
  // FIX: Ensure X-Sucursal-Id header is always a string value.
  // FIX: Use NaN guard on parseInt to prevent invalid sucursal IDs.
  const cambiarSucursal = useCallback((sucursalId) => {
    const id = parseInt(sucursalId, 10);
    if (isNaN(id)) return;
    setSucursalActiva(id);
    localStorage.setItem('sucursalActiva', String(id));
    api.defaults.headers.common['X-Sucursal-Id'] = String(id);

    // Calcular modo espectador
    // FIX: super_admin NUNCA entra en modo espectador — puede actuar en cualquier sucursal
    const esSuperAdmin = usuario?.rol === 'super_admin';
    const puedeVerOtras = esSuperAdmin || usuario?.rol === 'admin' || (usuario?.permisos || []).includes('*');
    setModoEspectador(puedeVerOtras && !esSuperAdmin && id !== usuario?.sucursalId);
  }, [usuario]);

  // FIX: Removed duplicate token refresh interceptor from AuthContext.
  // Token refresh with race condition guard is now handled in api.js
  // to avoid double-refresh issues when multiple requests fail with 401.

  const tienePermiso = (permiso) => {
    if (!usuario || !usuario.permisos) return false;
    if (permiso === 'super_admin_only') return usuario?.rol === 'super_admin';
    if (permiso === 'admin_only') return usuario?.rol === 'super_admin' || usuario?.rol === 'admin';
    if (usuario.permisos.includes('*')) return true;
    if (usuario.permisos.includes(permiso)) return true;
    const base = permiso.split(':')[0];
    return usuario.permisos.includes(base);
  };

  // FIX: Incluir super_admin explícitamente
  const esAdmin = () => {
    return usuario?.rol === 'admin' || usuario?.rol === 'super_admin' || (usuario?.permisos || []).includes('*');
  };

  // Helper: es admin o gerente (para acciones supervisoras como aprobar gastos)
  const esAdminOGerente = () => {
    return esAdmin() || usuario?.rol === 'gerente';
  };

  const value = {
    usuario,
    sucursales,
    sucursalActiva,
    modoEspectador,
    isAuthenticated,
    loading,
    login,
    logout,
    cerrarTodasSesiones,
    cambiarSucursal,
    tienePermiso,
    esAdmin,
    esAdminOGerente
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
